import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Loader, Stack, Text } from '@vipso/shared';
import {
  logout,
  setAuthFromInit,
  setBootstrapped,
  setTenantContext,
  type OrgInfo,
} from '../store/authSlice';
import { mapInitAppPayload } from '../store/authUtils';
import type { RootState } from '../store';
import {
  DEMO_CONFIG,
  DEMO_ORGS,
  DEMO_TENANT,
  DEMO_THEME,
  buildInitPayload,
} from '../mock/mockData';

function resolveOrgId(org: OrgInfo | null): string | null {
  if (!org) return null;
  if (org.id != null && org.id !== '') return String(org.id);
  if (org.orgId != null && org.orgId !== '') return String(org.orgId);
  return null;
}

export function BootPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const started = useRef(false);

  const accessToken = useSelector((state: RootState) => state.auth.accessToken);
  const user = useSelector((state: RootState) => state.auth.user);
  const org = useSelector((state: RootState) => state.auth.org);
  const theme = useSelector((state: RootState) => state.auth.theme);
  const config = useSelector((state: RootState) => state.auth.config);
  const logoUrl = config?.logoUrl || theme?.logoUrl;

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    dispatch(
      setTenantContext({
        tenant: DEMO_TENANT,
        variantName: DEMO_TENANT.name,
        theme: DEMO_THEME,
        config: DEMO_CONFIG,
        tenantOrgs: DEMO_ORGS.map((o) => ({
          id: o.id,
          name: o.name,
          uniqueId: o.uniqueId,
          status: o.status,
        })),
      }),
    );

    const orgId = resolveOrgId(org);
    const hasSession = Boolean(accessToken && user?.id && orgId);

    if (!hasSession) {
      dispatch(setBootstrapped(true));
      navigate('/login', { replace: true });
      return;
    }

    try {
      const payload = buildInitPayload(orgId!, user?.phone ?? '');
      dispatch(
        setAuthFromInit(
          mapInitAppPayload(payload, {
            tenant: DEMO_TENANT,
            theme: DEMO_THEME,
            user,
          }),
        ),
      );
      dispatch(setBootstrapped(true));
      navigate('/dashboard', { replace: true });
    } catch {
      dispatch(logout());
      dispatch(setBootstrapped(true));
      navigate('/login', { replace: true });
    }
  }, [dispatch, navigate, accessToken, user, org]);

  return (
    <Stack
      align="center"
      justify="center"
      style={{ minHeight: '100vh', background: '#ffffff' }}
      gap="md"
    >
      <img
        src={logoUrl || '/brand/logo-sm.svg'}
        alt="VIPSo"
        style={{ width: 150, height: 200, objectFit: 'contain' }}
      />
      <Loader />
      <Text c="dimmed" size="sm">
        {t('boot.loadingDemo')}
      </Text>
    </Stack>
  );
}
